import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../auth/AuthContext";
import { AddQuickPaymentPayload, addQuickPayment } from "../services/bookings";
import BackButton from "./calendar/BackButton";
import { Customer } from "../types/customer";
import GlobalLoader from "./ui/GlobalLoader";
import { useToast } from "../hooks/useToast";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, CreditCard, Landmark, Wallet, Smartphone, LayoutGrid, Plus, Receipt, Calculator, CheckCircle2, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { cn } from "../lib/utils";

import { Service } from "../types/calendar";

interface FinalPaymentStepProps {
  selectedServices: Service[];
  selectedCustomer: Customer;
  appointmentTime: string;
  onRebook: () => void;
  bookingId?: number; // Added bookingId from parent component
  goBack: () => void;
  staffList: { id: number; name: string; color: string }[];
  stylist: string;
  consultation_form_ids?: number[];
}

const FinalPaymentStep: React.FC<FinalPaymentStepProps> = ({
  selectedServices,
  selectedCustomer,
  appointmentTime,
  onRebook,
  bookingId,
  goBack,
  staffList,
  stylist,
  consultation_form_ids,
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "";
  const toast = useToast();
  const [isPaid, setIsPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStylist, setSelectedStylist] = useState<string>(stylist);
  const [paymentMethod, setPaymentMethod] = useState<string>("terminal");

  const getInitials = (name: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || "??";
  };

  const products = [{ name: "Premium Hair Wax", price: 35 }];

  const productTotal = products.reduce((acc, p) => acc + p.price, 0);
  const basePrice = selectedServices.reduce((acc, s) => acc + Number(s.price), 0);
  const totalAmount = basePrice + productTotal;
  const paidAmount = 35; // Assuming fixed deposit for now, or fetch from booking
  const dueAmount = totalAmount - paidAmount;

  const handlePayment = async () => {
    setLoading(true);
    try {
      if (!bookingId) throw new Error("Booking ID is missing");

      const stylistObj = staffList.find((s) => s.name === selectedStylist);
      const employee_id = stylistObj?.id || 1;

      const payload: AddQuickPaymentPayload = {
        booking_id: bookingId,
        employee_id: employee_id,
        transaction_id: `TXN-${Date.now()}`,
        amount: dueAmount, // Use dynamic due amount
        status: "completed",
        company_id: 1,
        is_quick_payment: 1,
        consultation_form_ids: consultation_form_ids,
      };

      const response = await addQuickPayment(payload);

      console.log("Payment successful:", response);
      toast.success("Payment processed successfully!");
      setIsPaid(true);
    } catch (err: any) {
      console.error("Payment failed:", err);
      if (err.type === "serverValidation") {
        toast.error("Validation error: " + JSON.stringify(err.errors));
      } else if (err.type === "network") {
        toast.error(err.message);
      } else {
        toast.error("Payment failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full bg-[#F9F9F9] overflow-hidden">
      {/* LEFT PANEL - Customer Profile */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="w-[35%] bg-white border-r border-gray-100 flex flex-col p-8 overflow-y-auto"
      >
        <div className="flex flex-col items-center text-center space-y-4 mb-10">
          <div className="w-24 h-24 rounded-full bg-[#885ABB]/10 flex items-center justify-center text-[#885ABB] text-3xl font-bold shadow-sm border-4 border-white">
            {getInitials(selectedCustomer.name)}
          </div>
          <div>
            <h3 className="text-xl font-[600] text-[#402B69] mb-1">{selectedCustomer.name}</h3>
            <p className="text-sm text-gray-400 font-medium">{selectedCustomer.email || "No email provided"}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-3">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Contact Info</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 text-sm text-gray-500 font-medium">
                <Phone size={14} className="text-[#885ABB]" /> <span>{selectedCustomer.phone || "Not provided"}</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 text-sm text-gray-500 font-medium">
                <Mail size={14} className="text-[#885ABB]" /> <span>{selectedCustomer.email || "Not provided"}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Appointment Details</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 rounded-xl bg-[#885ABB]/5 text-sm text-[#885ABB] font-bold">
                <div className="flex items-center gap-3">
                  <Clock size={14} /> <span>{appointmentTime}</span>
                </div>
                <span className="text-[10px] uppercase">Slot</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Right Side: Appointment Details */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex-1 bg-[#F9F9F9] flex flex-col overflow-hidden relative"
      >
        {/* Internal Header for Right Panel */}
        <div className="px-8 pt-8 pb-5 bg-white border-b border-gray-100 shadow-sm relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[22px] font-[600] text-[#402B69]">Complete Payment</h2>
              <p className="text-[10px] text-gray-400 mt-0.5 font-semibold uppercase">
                Secure Checkout & Receipt
              </p>
            </div>
            <BackButton onClick={goBack} />
          </div>

          <div className="flex justify-between items-center bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-3">
              <span className="text-[13px] font-bold text-[#402B69] uppercase tracking-tight">Assigned Stylist</span>
              <Select value={selectedStylist} onValueChange={setSelectedStylist}>
                <SelectTrigger className="w-40 h-10 border border-[#C4A6E1] rounded-lg text-sm font-semibold text-[#402B69] bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-100 rounded-xl shadow-xl">
                  {staffList.map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-[#885ABB]">{appointmentTime}</div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Scheduled Time</div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-8 p-8 pr-2 custom-scrollbar">
          {/* Payment Method Selection */}
          <section className="space-y-4">
            <div className="flex justify-between items-center px-1">
              <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Payment Method</h4>
              <span className="text-[10px] font-bold text-[#29BF6A] bg-[#29BF6A]/5 px-2.5 py-1 rounded-full border border-[#29BF6A]/10">Action Required</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { id: 'terminal', label: 'Terminal', icon: Landmark },
                { id: 'cash', label: 'Cash', icon: Wallet },
                { id: 'gift', label: 'Gift Card', icon: CreditCard },
                { id: 'split', label: 'Split', icon: LayoutGrid },
                { id: 'qr', label: 'QR Code', icon: Smartphone },
                { id: 'other', label: 'Other', icon: Plus },
              ].map((method) => {
                const Icon = method.icon;
                const isSelected = paymentMethod === method.id;
                return (
                  <button
                    key={method.id}
                    onClick={() => setPaymentMethod(method.id)}
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-200",
                      isSelected
                        ? "border-[#885ABB] bg-[#885ABB]/5 text-[#885ABB]"
                        : "border-gray-50 bg-white text-gray-400 hover:border-gray-100"
                    )}
                  >
                    <Icon size={18} />
                    <span className="text-[13px] font-bold uppercase tracking-tight">{method.label}</span>
                    {isSelected && <CheckCircle2 size={14} className="ml-auto" />}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Billing Card */}
          <section className="space-y-4">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Order Recap</h4>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-6 space-y-5">
                {selectedServices.map((service, idx) => (
                  <div key={idx} className="flex justify-between items-start">
                    <div className="flex gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#885ABB] mt-1.5" />
                      <p className="text-[14px] font-[600] text-[#402B69] capitalize">{service.name}</p>
                    </div>
                    <span className="text-[14px] font-bold text-[#402B69]">{currencySymbol}{Number(service.price).toFixed(2)}</span>
                  </div>
                ))}

                {products.map((product, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <div className="flex gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5" />
                      <p className="text-[14px] font-[600] text-[#402B69]">{product.name}</p>
                    </div>
                    <span className="text-[14px] font-bold text-[#402B69]">{currencySymbol}{product.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="p-6 bg-[#F9F9F9] border-t border-gray-100 space-y-3">
                <div className="flex justify-between text-[13px] font-medium text-gray-500">
                  <span>Initial Total</span>
                  <span>{currencySymbol}{totalAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[13px] text-[#29BF6A] font-medium">
                  <span>Paid Balance</span>
                  <span>- {currencySymbol}{paidAmount.toFixed(2)}</span>
                </div>
                <div className="pt-5 border-t border-gray-200/60 flex justify-between items-center">
                  <span className="text-[18px] font-[600] text-[#402B69]">Amount to Pay</span>
                  <span className="text-3xl font-extrabold text-[#29BF6A]">{currencySymbol}{dueAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </section>

          <div className="flex gap-4">
            <button className="flex-1 h-11 bg-[#885ABB]/5 text-[#885ABB] rounded-xl font-bold text-xs uppercase tracking-wide hover:bg-[#885ABB]/10 transition-colors border border-[#885ABB]/10">
              + Add Service
            </button>
            <button className="flex-1 h-11 bg-[#885ABB]/5 text-[#885ABB] rounded-xl font-bold text-xs uppercase tracking-wide hover:bg-[#885ABB]/10 transition-colors border border-[#885ABB]/10">
              + Add Product
            </button>
          </div>

          <section className="space-y-3">
            <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Note</h4>
            <textarea
              placeholder="Any specific comments for this booking..."
              className="w-full h-24 p-4 bg-white border border-gray-100 rounded-2xl text-[14px] font-medium resize-none focus:outline-none focus:ring-2 focus:ring-[#885ABB]/10 transition-all placeholder:text-gray-300 shadow-sm"
            />
          </section>
        </div>

        {/* Sticky Action Footer */}
        <div className="p-6 bg-white border-t border-gray-50 flex gap-4">
          {!isPaid ? (
            <>
              <button
                className="flex-1 h-12 bg-white border border-red-100 text-[#BB5A5E] rounded-xl font-bold hover:bg-red-50 transition-all text-sm uppercase tracking-wide"
              >
                Cancel
              </button>

              <button
                onClick={handlePayment}
                disabled={loading}
                className="flex-[2] h-12 bg-[#29BF6A] text-white rounded-xl font-bold hover:bg-[#22a35a] shadow-lg shadow-[#29BF6A]/20 transition-all flex items-center justify-center gap-2 group text-sm uppercase tracking-wider"
              >
                <Calculator size={18} />
                <span>{loading ? "Processing..." : "Pay Now"}</span>
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onRebook}
                className="flex-1 h-12 bg-[#885ABB] text-white rounded-xl font-bold hover:bg-[#7748A4] shadow-lg shadow-[#885ABB]/10 transition-all flex items-center justify-center gap-2 text-sm uppercase tracking-wider"
              >
                <CalendarIcon size={18} />
                Rebook
              </button>

              <div className="flex-[2] h-12 bg-white border border-[#29BF6A]/20 rounded-xl font-bold flex items-center justify-center gap-3 text-[#29BF6A] text-sm uppercase tracking-widest">
                <CheckCircle2 size={20} className="text-[#29BF6A]" />
                Transaction Complete
              </div>
            </>
          )}
        </div>
      </motion.div>

      {loading && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
          <GlobalLoader
            initialPageLoad={false}
            description="Processing payment..."
            compensateSidebar={false}
          />
        </div>
      )}
    </div>
  );
};

export default FinalPaymentStep;
